/**
 * ShareProfileModal — public-facing "Share this doctor" modal.
 *
 * iter100.17 (2026-02-28): We deliberately avoided cramming a QR code
 * directly into the doctor's identity band because QR codes serve a
 * *physical → digital* handoff (business card, poster, clinic reception),
 * not a screen-to-screen one. Instead, a small "Share" icon in the
 * header opens THIS modal, which gives the doctor / patient every share
 * surface they might need in one place:
 *
 *   • QR code       — for showing on-screen to a nearby phone
 *   • Copy link     — universal fallback
 *   • WhatsApp      — dominant in India, biggest driver
 *   • SMS           — for non-WhatsApp patients / older demographics
 *   • Native share  — bubble up to the OS share sheet where supported
 *
 * The modal is intentionally accent-aware (matches the doctor's color
 * theme) but otherwise style-neutral so it can be dropped into any
 * public page — not just the DoctorLandingPage.
 */
import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
    X, Copy, CheckCircle, WhatsappLogo, ChatCircleText, ShareNetwork,
    Link as LinkIcon, DeviceMobile, Download,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareProfileModal({
    open,
    onClose,
    url,
    title,          // e.g. "Dr. James Wilson"
    subtitle,       // e.g. "Certified Invisique™ Provider"
    accent,         // { base, soft, ring, text } from parent
    shareMessage,   // Optional pre-composed message body
}) {
    const [copied, setCopied] = useState(false);
    const qrRef = useRef(null);

    // Reset copied flag whenever the modal reopens
    useEffect(() => {
        if (open) setCopied(false);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const themeBase = accent?.base || "hsl(192 90% 33%)";
    const themeSoft = accent?.soft || "hsl(192 90% 96%)";
    const themeRing = accent?.ring || "hsl(192 90% 88%)";
    const themeText = accent?.text || "hsl(192 90% 25%)";

    const defaultMsg = shareMessage
        || `Check out ${title || "this doctor"} on Invisique™ — book a consultation or preview your smile with AI: ${url}`;
    const smsBody = encodeURIComponent(defaultMsg);
    const waHref = `https://wa.me/?text=${encodeURIComponent(defaultMsg)}`;
    const smsHref = `sms:?&body=${smsBody}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Couldn't copy — long-press the link to copy manually.");
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) {
            toast.info("Native share not available — use one of the options below.");
            return;
        }
        try {
            await navigator.share({
                title: title || "Invisique™ Provider",
                text: defaultMsg,
                url,
            });
        } catch { /* user cancelled — no-op */ }
    };

    const downloadQR = () => {
        // Grab the inline SVG, serialize, and download as PNG-shaped SVG file.
        const svg = qrRef.current?.querySelector("svg");
        if (!svg) return;
        const serializer = new XMLSerializer();
        const source = serializer.serializeToString(svg);
        const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
        const dlUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = dlUrl;
        a.download = `${(title || "invisique-doctor").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-qr.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(dlUrl);
    };

    const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

    return (
        <div
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-6"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            role="dialog"
            aria-modal="true"
            data-testid="share-profile-modal"
        >
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-2 fade-in duration-300">

                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: themeBase }}>
                            Share profile
                        </div>
                        <div className="font-display text-lg font-bold text-slate-900 leading-tight mt-0.5 truncate">
                            {title || "Share this profile"}
                        </div>
                        {subtitle && (
                            <div className="text-[12px] text-slate-600 mt-0.5 truncate">{subtitle}</div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0"
                        aria-label="Close"
                        data-testid="share-profile-modal-close"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>

                {/* QR panel */}
                <div className="px-5 pt-5">
                    <div
                        ref={qrRef}
                        className="mx-auto w-fit rounded-2xl border p-4"
                        style={{ borderColor: themeRing, backgroundColor: themeSoft }}
                        data-testid="share-profile-qr"
                    >
                        <QRCodeSVG
                            value={url}
                            size={168}
                            fgColor={themeBase}
                            bgColor="#ffffff"
                            level="M"
                            includeMargin={false}
                        />
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                        <DeviceMobile size={13} weight="duotone" />
                        Point a phone camera at the QR to open this profile
                    </div>
                    <button
                        type="button"
                        onClick={downloadQR}
                        className="mt-2 mx-auto flex items-center gap-1.5 text-[12px] font-semibold hover:underline"
                        style={{ color: themeText }}
                        data-testid="share-profile-qr-download"
                    >
                        <Download size={13} weight="bold" /> Download QR
                    </button>
                </div>

                {/* Link row */}
                <div className="px-5 pt-5">
                    <div className="flex items-stretch gap-2">
                        <div
                            className="flex-1 min-w-0 flex items-center gap-2 h-11 px-3 rounded-lg border bg-slate-50"
                            style={{ borderColor: "hsl(215 20% 90%)" }}
                        >
                            <LinkIcon size={14} weight="duotone" className="text-slate-500 shrink-0" />
                            <span className="text-[13px] text-slate-700 truncate font-mono" data-testid="share-profile-url">
                                {url}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={copyLink}
                            className="h-11 px-4 rounded-lg font-semibold text-[13px] inline-flex items-center gap-1.5 transition-all"
                            style={copied
                                ? { backgroundColor: "hsl(142 60% 40%)", color: "white" }
                                : { backgroundColor: themeBase, color: "white" }}
                            data-testid="share-profile-copy-link"
                        >
                            {copied ? (
                                <>
                                    <CheckCircle size={15} weight="fill" /> Copied
                                </>
                            ) : (
                                <>
                                    <Copy size={14} weight="bold" /> Copy
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Share targets */}
                <div className="p-5 grid grid-cols-3 gap-2">
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-16 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex flex-col items-center justify-center gap-1 transition-all"
                        data-testid="share-profile-whatsapp"
                    >
                        <WhatsappLogo size={22} weight="fill" style={{ color: "#25D366" }} />
                        <span className="text-[11px] font-semibold text-slate-700">WhatsApp</span>
                    </a>
                    <a
                        href={smsHref}
                        className="h-16 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex flex-col items-center justify-center gap-1 transition-all"
                        data-testid="share-profile-sms"
                    >
                        <ChatCircleText size={22} weight="fill" style={{ color: themeBase }} />
                        <span className="text-[11px] font-semibold text-slate-700">SMS</span>
                    </a>
                    <button
                        type="button"
                        onClick={nativeShare}
                        disabled={!canNativeShare}
                        className="h-16 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-45 disabled:cursor-not-allowed"
                        data-testid="share-profile-native"
                        title={canNativeShare ? "Open device share sheet" : "Not supported on this device"}
                    >
                        <ShareNetwork size={22} weight="fill" style={{ color: themeBase }} />
                        <span className="text-[11px] font-semibold text-slate-700">More…</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
